import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Clock, MapPin, Phone, Mail, FileText, Wrench, CheckCircle2,
  RefreshCw, DollarSign, XCircle, Gavel, AlertTriangle, ThumbsUp, ThumbsDown,
  SearchCheck, Trash2, Check,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";
import AttachmentGallery from "@/components/AttachmentGallery";
import Loader from "@/components/Loader";
import { useAuth } from "@/context/AuthContext";
import { ADMIN_ROLES } from "@/lib/constants";
import api from "@/lib/apiClient";

const RES_OPTIONS = [
  { key: "replace", label: "Replace Battery", icon: RefreshCw, tint: "text-emerald-600" },
  { key: "service", label: "Provide Service", icon: Wrench, tint: "text-blue-600" },
  { key: "refund", label: "Money Refund", icon: DollarSign, tint: "text-violet-600" },
  { key: "inspect", label: "Physical Inspection", icon: SearchCheck, tint: "text-cyan-600" },
];

const STEPS = ["Submitted", "Approved", "Resolution", "In Progress", "Completed"];
function stepIndex(status) {
  if (status === "rejected") return -1;
  if (status === "submitted") return 0;
  if (status === "approved") return 1;
  if (["replace_approved", "refund_approved", "service_pending", "inspection_scheduled"].includes(status)) return 2;
  if (status === "service_assigned") return 3;
  if (["service_completed", "closed"].includes(status)) return 4;
  return 0;
}

function StatusStepper({ status }) {
  const idx = stepIndex(status);
  if (idx === -1) {
    return (
      <Card className="p-5 border-l-4 border-l-red-500 bg-red-50/50" data-testid="status-stepper">
        <div className="flex items-center gap-2 text-red-700 font-semibold"><XCircle className="h-5 w-5" /> This claim was rejected and will not be processed.</div>
      </Card>
    );
  }
  return (
    <Card className="p-5" data-testid="status-stepper">
      <div className="flex items-center">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className="flex flex-col items-center gap-1.5 min-w-0">
              <div className={`h-9 w-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 transition-colors ${i <= idx ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
                {i < idx ? <Check className="h-4 w-4" /> : i + 1}
              </div>
              <span className={`text-[11px] font-medium text-center ${i <= idx ? "text-foreground" : "text-muted-foreground"}`}>{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 mx-1 -mt-5 ${i < idx ? "bg-primary" : "bg-border"}`} />}
          </React.Fragment>
        ))}
      </div>
    </Card>
  );
}

export default function ClaimDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const isSP = user?.role === "service_provider";

  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [centers, setCenters] = useState([]);

  const [resType, setResType] = useState("");
  const [tat, setTat] = useState("");
  const [remarks, setRemarks] = useState("");
  const [refund, setRefund] = useState("");
  const [decOpen, setDecOpen] = useState(false);

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectRemarks, setRejectRemarks] = useState("");

  const [assignOpen, setAssignOpen] = useState(false);
  const [centerId, setCenterId] = useState("");

  const [completeOpen, setCompleteOpen] = useState(false);
  const [notes, setNotes] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/claims/${id}`);
      setClaim(res.data);
    } catch {
      toast.error("Could not load claim.");
      navigate("/claims");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const loadCenters = async (claimData) => {
    const res = await api.get("/service-centers", { params: { pincode: claimData?.pincode, city: claimData?.city } });
    setCenters(res.data);
    if (res.data[0]) setCenterId(res.data[0].id);
  };

  const review = async (action, note) => {
    try {
      await api.post(`/claims/${id}/review`, { action, remarks: note || null });
      toast.success(action === "approve" ? "Claim approved — customer notified." : "Claim rejected — customer notified.");
      setRejectOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed."); }
  };

  const submitDecision = async () => {
    if (!resType) { toast.error("Select a resolution."); return; }
    try {
      await api.post(`/claims/${id}/decision`, {
        resolution_type: resType,
        tat_days: tat ? Number(tat) : null,
        remarks: remarks || null,
        refund_amount: refund ? Number(refund) : null,
      });
      toast.success("Resolution recorded and customer notified.");
      setDecOpen(false);
      if (resType === "service") { await loadCenters(claim); setAssignOpen(true); }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed.");
    }
  };

  const submitAssign = async () => {
    if (!centerId) { toast.error("Select a service center."); return; }
    try {
      await api.post(`/claims/${id}/assign`, { service_center_id: centerId });
      toast.success("Service assigned & provider notified.");
      setAssignOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed."); }
  };

  const submitComplete = async () => {
    if (!notes) { toast.error("Add completion notes."); return; }
    try {
      await api.post(`/claims/${id}/complete`, { completion_notes: notes });
      toast.success("Service marked complete.");
      setCompleteOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed."); }
  };

  const closeClaim = async () => {
    try { await api.post(`/claims/${id}/close`); toast.success("Claim closed."); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed."); }
  };

  const deleteClaim = async () => {
    try { await api.delete(`/claims/${id}`); toast.success("Claim deleted."); navigate("/claims"); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed."); }
  };

  if (loading || !claim) return <Loader label="Loading claim..." />;

  const InfoRow = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-2 text-sm">
      <Icon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
      <span className="text-muted-foreground">{label}:</span>
      <span className="font-medium">{value || "—"}</span>
    </div>
  );

  const customEntries = Object.entries(claim.custom || {}).filter(([, v]) => v);

  return (
    <div className="max-w-5xl space-y-6" data-testid="claim-detail-page">
      <button onClick={() => navigate("/claims")} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="back-button">
        <ArrowLeft className="h-4 w-4" /> Back to claims
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-display text-3xl font-extrabold text-primary">{claim.claim_no}</h1>
            <StatusBadge status={claim.status} />
            {claim.tat_breached && (
              <span className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-700 border border-red-200 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider animate-pulse" data-testid="tat-breach-badge">
                <AlertTriangle className="h-3 w-3" /> TAT Breached
              </span>
            )}
          </div>
          <p className="text-muted-foreground mt-1">
            Battery {claim.battery_serial} · {claim.battery_model} · <span className="font-semibold text-foreground">Claim type: {claim.claim_type || "—"}</span>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && claim.status === "submitted" && (
            <>
              <Button onClick={() => review("approve")} className="gap-2 bg-emerald-600 hover:bg-emerald-700" data-testid="approve-button">
                <ThumbsUp className="h-4 w-4" /> Approve
              </Button>
              <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="gap-2 text-red-600 border-red-200 hover:bg-red-50" data-testid="reject-button">
                    <ThumbsDown className="h-4 w-4" /> Reject
                  </Button>
                </DialogTrigger>
                <DialogContent data-testid="reject-dialog">
                  <DialogHeader><DialogTitle className="font-display">Reject Claim</DialogTitle>
                    <DialogDescription>Provide a reason; the customer will be notified.</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-1.5">
                    <Label>Reason (sent to customer)</Label>
                    <Textarea value={rejectRemarks} onChange={(e) => setRejectRemarks(e.target.value)} placeholder="Explain why the claim doesn't qualify..." data-testid="reject-remarks-input" />
                  </div>
                  <DialogFooter>
                    <Button variant="destructive" onClick={() => review("reject", rejectRemarks)} data-testid="confirm-reject-button">Reject Claim</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </>
          )}

          {isAdmin && claim.status === "approved" && (
            <Dialog open={decOpen} onOpenChange={setDecOpen}>
              <DialogTrigger asChild>
                <Button className="gap-2" data-testid="decide-button"><Gavel className="h-4 w-4" /> Set Resolution</Button>
              </DialogTrigger>
              <DialogContent data-testid="decision-dialog">
                <DialogHeader><DialogTitle className="font-display">Claim Resolution</DialogTitle>
                  <DialogDescription>Choose how the company will resolve this claim and set the TAT.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-2">
                    {RES_OPTIONS.map((o) => (
                      <button key={o.key} onClick={() => setResType(o.key)} data-testid={`res-${o.key}`}
                        className={`flex items-center gap-2 rounded-lg border p-3 text-sm font-medium transition-all ${resType === o.key ? "border-primary bg-accent" : "border-border hover:bg-accent/50"}`}>
                        <o.icon className={`h-4 w-4 ${o.tint}`} /> {o.label}
                      </button>
                    ))}
                  </div>
                  <div className="space-y-1.5">
                    <Label>TAT — Total Allotted Time (days)</Label>
                    <Input type="number" value={tat} onChange={(e) => setTat(e.target.value)} placeholder="e.g. 7" data-testid="tat-input" />
                  </div>
                  {resType === "refund" && (
                    <div className="space-y-1.5">
                      <Label>Refund Amount (₹)</Label>
                      <Input type="number" value={refund} onChange={(e) => setRefund(e.target.value)} placeholder="e.g. 8500" data-testid="refund-input" />
                    </div>
                  )}
                  <div className="space-y-1.5">
                    <Label>Remarks (sent to customer)</Label>
                    <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Optional message..." />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={submitDecision} data-testid="confirm-decision-button">Confirm & Notify</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

          {isAdmin && claim.status === "service_pending" && (
            <Button className="gap-2" onClick={async () => { await loadCenters(claim); setAssignOpen(true); }} data-testid="assign-service-button">
              <Wrench className="h-4 w-4" /> Assign Service Center
            </Button>
          )}

          {(isSP || isAdmin) && claim.status === "service_assigned" && (
            <Button className="gap-2" onClick={() => setCompleteOpen(true)} data-testid="complete-service-button">
              <CheckCircle2 className="h-4 w-4" /> Mark Service Complete
            </Button>
          )}

          {isAdmin && ["replace_approved", "refund_approved", "inspection_scheduled", "service_completed"].includes(claim.status) && (
            <Button variant="secondary" onClick={closeClaim} data-testid="close-claim-button">Close Claim</Button>
          )}

          {isAdmin && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="icon" className="text-red-600" data-testid="delete-claim-button"><Trash2 className="h-4 w-4" /></Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader><AlertDialogTitle>Delete claim {claim.claim_no}?</AlertDialogTitle>
                  <AlertDialogDescription>This permanently removes the claim and its notifications.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={deleteClaim} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <StatusStepper status={claim.status} />

      {claim.resolution_type && claim.resolution_type !== "reject" && (
        <Card className="p-5 border-l-4 border-l-primary" data-testid="resolution-banner">
          <div className="flex flex-wrap items-center gap-6">
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Decision</p>
              <p className="font-display font-bold text-lg capitalize">{claim.resolution_type}</p>
            </div>
            {claim.tat_days && (
              <div><p className="text-xs uppercase tracking-wider text-muted-foreground">TAT</p><p className="font-display font-bold text-lg">{claim.tat_days} days</p></div>
            )}
            {claim.refund_amount && (
              <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Refund</p><p className="font-display font-bold text-lg">₹{claim.refund_amount}</p></div>
            )}
            {claim.service_center_name && (
              <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Service Center</p><p className="font-display font-bold text-lg">{claim.service_center_name}</p></div>
            )}
          </div>
          {claim.admin_remarks && <p className="mt-3 text-sm text-muted-foreground">{claim.admin_remarks}</p>}
          {claim.completion_notes && <p className="mt-3 text-sm"><span className="font-semibold">Completion:</span> {claim.completion_notes}</p>}
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="font-display font-bold mb-4">Customer & Issue</h3>
          <div className="space-y-2.5">
            <InfoRow icon={FileText} label="Name" value={`${claim.customer_name} (${claim.customer_type})`} />
            <InfoRow icon={Phone} label="Phone" value={claim.customer_phone} />
            <InfoRow icon={Mail} label="Email" value={claim.customer_email} />
            <InfoRow icon={MapPin} label="Location" value={[claim.city, claim.pincode].filter(Boolean).join(", ")} />
            <InfoRow icon={FileText} label="Invoice" value={claim.invoice_no} />
            <div className="pt-3 border-t mt-3">
              <p className="text-sm font-semibold">{claim.issue_type}</p>
              <p className="text-sm text-muted-foreground mt-1">{claim.issue_description}</p>
            </div>
            {customEntries.length > 0 && (
              <div className="pt-3 border-t mt-3 space-y-1.5" data-testid="claim-custom-fields">
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Additional Details</p>
                {customEntries.map(([k, v]) => (
                  <div key={k} className="flex gap-2 text-sm"><span className="text-muted-foreground capitalize">{k.replace(/^cf_/, "").replace(/_/g, " ")}:</span><span className="font-medium">{String(v)}</span></div>
                ))}
              </div>
            )}
            <div className="pt-3 border-t mt-3">
              <AttachmentGallery attachments={claim.attachments || []} />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="font-display font-bold mb-4">Live Status & Timeline</h3>
          <div className="space-y-4">
            {(claim.timeline || []).slice().reverse().map((t, i) => (
              <div key={i} className="flex gap-3" data-testid={`timeline-${i}`}>
                <div className="flex flex-col items-center">
                  <div className="h-2.5 w-2.5 rounded-full bg-primary mt-1.5" />
                  {i < claim.timeline.length - 1 && <div className="w-px flex-1 bg-border my-1" />}
                </div>
                <div className="pb-2">
                  <p className="text-sm font-medium">{t.event}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                    <Clock className="h-3 w-3" /> {new Date(t.ts).toLocaleString()} · {t.by}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent data-testid="assign-dialog">
          <DialogHeader><DialogTitle className="font-display">Assign Nearest Service Center</DialogTitle>
            <DialogDescription>Pick a service center; the provider and customer will be notified.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Service Center (sorted by proximity to {claim.city || claim.pincode})</Label>
            <Select value={centerId} onValueChange={setCenterId}>
              <SelectTrigger data-testid="center-select"><SelectValue placeholder="Select center" /></SelectTrigger>
              <SelectContent>
                {centers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} — {c.city} ({c.pincode})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter><Button onClick={submitAssign} data-testid="confirm-assign-button">Assign & Notify</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={completeOpen} onOpenChange={setCompleteOpen}>
        <DialogContent data-testid="complete-dialog">
          <DialogHeader><DialogTitle className="font-display">Complete Service</DialogTitle>
            <DialogDescription>Record what was done; the customer will be notified.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Completion Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Describe the work performed..." data-testid="completion-notes-input" />
          </div>
          <DialogFooter><Button onClick={submitComplete} data-testid="confirm-complete-button">Submit</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
